// Pure helpers for the RecorderProgress component.
//
// The recorder agent runs as a sync job that writes messages to a conversation
// — every tool call lands as a `RoleToolCall` message (with `toolUseContent`
// JSON of the call args), followed eventually by a `RoleToolResponse` message
// (with `toolUseId` pointing back). We pair them and render a flat timeline.

import type { Message } from '@/types';

// Tool fields the backend stamps on tool-call / tool-response messages but
// aren't in the shared Message type yet. Cast at the call site.
export type ToolMessage = Message & {
  toolUseId?: string;
  toolUseContent?: string;
  toolName?: string;
};

export type ToolStatus = 'running' | 'success' | 'error';

export interface ToolEvent {
  id: string; // toolUseId
  name: string; // raw tool name (record_step, browser_cli, …)
  prettyName: string;
  description: string;
  status: ToolStatus;
  errorText?: string;
  // Parsed response body, kept for finalize/commit_recipe so the caller can
  // read `committed`, `blockers`, etc.
  response?: Record<string, unknown>;
}

// Fallback labels when we can't infer a more specific action from the input.
const TOOL_LABELS: Record<string, string> = {
  get_credential: 'Loading credentials',
  record_step: 'Recording step',
  browser_cli: 'Inspecting page',
  commit_recipe: 'Saving login recipe',
  finalize: 'Finalizing',
  query: 'Querying graph',
  schema: 'Reading schema',
};

export function buildToolTimeline(messages: Message[]): ToolEvent[] {
  const responsesByCallId = new Map<string, ToolMessage>();
  for (const msg of messages) {
    const cast = msg as ToolMessage;
    if (msg.role === 'tool response' && cast.toolUseId) {
      responsesByCallId.set(cast.toolUseId, cast);
    }
  }

  const events: ToolEvent[] = [];
  for (const msg of messages) {
    if (msg.role !== 'tool call') continue;
    const cast = msg as ToolMessage;
    const callId = cast.toolUseId;
    if (!callId) continue;

    const callData = parseJSON(cast.toolUseContent ?? msg.content);
    const toolName = readToolName(callData) ?? 'tool';
    const input = readToolInput(callData);

    const resp = responsesByCallId.get(callId);
    const responseData = resp ? parseJSON(resp.content) : null;
    const status: ToolStatus = !resp
      ? 'running'
      : isErrorPayload(responseData)
        ? 'error'
        : 'success';

    events.push({
      id: callId,
      name: toolName,
      prettyName: prettyNameFor(toolName, input),
      description: describeToolCall(toolName, input),
      status,
      errorText:
        status === 'error' ? extractErrorText(responseData) : undefined,
      response: isPlainObject(responseData) ? responseData : undefined,
    });
  }

  return events;
}

// prettyNameFor returns a layman-friendly label for a tool call. Falls back to
// TOOL_LABELS when the input doesn't carry enough info to specialize.
function prettyNameFor(name: string, input: unknown): string {
  if (name === 'record_step' && isPlainObject(input))
    return recordStepPrettyName(input.step);
  if (name === 'browser_cli' && isPlainObject(input))
    return browserCliPrettyName(input);
  return TOOL_LABELS[name] ?? name;
}

function recordStepPrettyName(step: unknown): string {
  if (!isPlainObject(step) || typeof step.kind !== 'string')
    return 'Recording step';
  switch (step.kind) {
    case 'navigate':
      return 'Opening page';
    case 'click':
      return 'Clicking element';
    case 'type': {
      const placeholder = extractPlaceholder(step.value);
      return placeholder ? `Entering ${placeholder}` : 'Typing into field';
    }
    case 'totp':
      return 'Entering 2FA code';
    case 'cookie':
      return 'Capturing session cookies';
    case 'capture':
      return typeof step.name === 'string'
        ? `Capturing header ${step.name}`
        : 'Capturing header';
    case 'extract':
      return 'Extracting value';
    case 'http':
      return 'Making HTTP request';
    case 'load_request':
    case 'network_get':
      return 'Replaying captured request';
    default:
      return 'Recording step';
  }
}

function browserCliPrettyName(input: Record<string, unknown>): string {
  if (typeof input.command !== 'string') return 'Inspecting page';
  switch (input.command) {
    case 'navigate':
      return 'Navigating browser';
    case 'elements':
      return 'Inspecting page elements';
    case 'screenshot':
      return 'Taking screenshot';
    case 'network_clear':
      return 'Clearing network capture';
    case 'network_list':
      return 'Listing network requests';
    case 'network_get':
      return 'Reading network request';
    case 'dom':
      return 'Reading page DOM';
    default:
      return 'Inspecting page';
  }
}

// Extracts the placeholder name from a `{{username}}` style value.
function extractPlaceholder(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = value.match(/^\{\{(.+?)\}\}$/);
  return match ? match[1] : null;
}

// ---- describe helpers (kept here so they're unit-testable) ----

export function describeToolCall(name: string, input: unknown): string {
  if (!isPlainObject(input)) return '';

  switch (name) {
    case 'record_step':
      return describeRecordStep(input.step);
    case 'browser_cli':
      return describeBrowserCli(input);
    case 'commit_recipe':
      return typeof input.label === 'string' ? input.label : '';
    case 'finalize':
      return typeof input.rationale === 'string' ? input.rationale : '';
    case 'query': {
      const node = isPlainObject(input.node) ? input.node : null;
      const labels = node && Array.isArray(node.labels) ? node.labels : [];
      return labels.join(', ');
    }
    case 'schema':
      return Array.isArray(input.entity_types)
        ? input.entity_types.join(', ')
        : '';
    default:
      return '';
  }
}

function describeRecordStep(step: unknown): string {
  if (!isPlainObject(step)) return '';
  const kind = typeof step.kind === 'string' ? step.kind : '';
  if (!kind) return '';

  const s = step as Record<string, unknown>;
  const sel = typeof s.selector === 'string' ? s.selector : '';
  const url = typeof s.url === 'string' ? s.url : '';

  switch (kind) {
    case 'navigate':
      return `navigate ${url}`.trim();
    case 'click':
      return `click ${sel}`.trim();
    case 'type': {
      const value = typeof s.value === 'string' ? s.value : '';
      return `type ${value} into ${sel}`.trim();
    }
    case 'cookie':
      return 'capture cookies';
    case 'capture':
      return typeof s.name === 'string'
        ? `capture header ${s.name}`
        : 'capture header';
    case 'extract': {
      const from = typeof s.from === 'string' ? s.from : '';
      const saveAs = typeof s.save_as === 'string' ? s.save_as : '';
      return `extract from ${from} → ${saveAs}`.trim();
    }
    case 'http': {
      const method = typeof s.method === 'string' ? s.method : 'GET';
      return `http ${method} ${url}`.trim();
    }
    case 'network_get':
      return `network_get ${url}`.trim();
    default:
      return kind;
  }
}

function describeBrowserCli(input: Record<string, unknown>): string {
  const cmd = typeof input.command === 'string' ? input.command : '';
  if (!cmd) return '';
  const params = isPlainObject(input.params) ? input.params : null;
  if (cmd === 'navigate' && typeof params?.url === 'string')
    return `navigate ${params.url}`;
  if (cmd === 'elements' && typeof params?.selector === 'string')
    return `find ${params.selector}`;
  if (cmd === 'network_get' && typeof params?.url === 'string')
    return `inspect ${params.url}`;
  return cmd;
}

// ---- low-level helpers ----

function readToolName(callData: unknown): string | null {
  if (!isPlainObject(callData)) return null;
  if (typeof callData.Name === 'string') return callData.Name;
  if (typeof callData.name === 'string') return callData.name;
  if (typeof callData.tool === 'string') return callData.tool;
  return null;
}

function readToolInput(callData: unknown): unknown {
  if (!isPlainObject(callData)) return null;
  return callData.Input ?? callData.input ?? callData.params ?? null;
}

function isErrorPayload(data: unknown): boolean {
  return isPlainObject(data) && 'error' in data && data.error !== '';
}

function extractErrorText(data: unknown): string | undefined {
  if (!isPlainObject(data)) return undefined;
  const err = data.error;
  if (typeof err === 'string' && err) return err;
  if (err != null) return JSON.stringify(err);
  return undefined;
}

function parseJSON(raw: string | undefined): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ---- finalize payload reading ----

export interface RecorderResult {
  blocked: boolean;
  label?: string;
  rationale?: string;
  blockers?: string[];
}

// extractRecorderResult reads the finalize tool's response payload, which the
// recorder agent shapes as { rationale, blocked?, label?, blockers? } per the
// finalize_schema in agents/recorder.md. blocked omitted/false = success.
export function extractRecorderResult(event: ToolEvent): RecorderResult | null {
  if (event.name !== 'finalize') return null;
  if (event.status === 'running') return null;
  const r = event.response ?? {};
  return {
    blocked: r.blocked === true,
    label: typeof r.label === 'string' ? r.label : undefined,
    rationale: typeof r.rationale === 'string' ? r.rationale : undefined,
    blockers:
      Array.isArray(r.blockers) && r.blockers.every(b => typeof b === 'string')
        ? (r.blockers as string[])
        : undefined,
  };
}
